/**
 * Roadmap sync.
 *
 * Progress, time and goals live on the device first — the roadmap itself is
 * reproducible from canonical facts, so this is the learner's own record. To
 * keep that record consistent across devices we mirror it into
 * `roadmap_state` (one row per learner) and merge on sign-in.
 *
 * Merge rules, chosen so nothing a learner did can be lost:
 *  - done events: union by (roadmap, milestone), keeping the earliest doneAt
 *  - time entries: union by id (ids are generated per stretch of effort)
 *  - goals: last write wins, by `updatedAt`
 */
import { supabase } from "@/integrations/supabase/client";
import {
  readProgress,
  readTimeLog,
  writeProgressLog,
  writeTimeLog,
  type MilestoneDone,
  type TimeEntry,
} from "@/lib/roadmapProgress";
import { readGoals, writeGoals, type RoadmapGoal } from "@/lib/roadmapGoals";

export interface RoadmapState {
  progress: MilestoneDone[];
  time: TimeEntry[];
  goals: RoadmapGoal[];
}

function localState(): RoadmapState {
  return { progress: readProgress(), time: readTimeLog(), goals: readGoals() };
}

export function mergeState(a: RoadmapState, b: RoadmapState): RoadmapState {
  const progress = new Map<string, MilestoneDone>();
  for (const e of [...a.progress, ...b.progress]) {
    const k = `${e.roadmapId}::${e.milestoneId}`;
    const seen = progress.get(k);
    if (!seen || e.doneAt < seen.doneAt) progress.set(k, e);
  }

  const time = new Map<string, TimeEntry>();
  for (const e of [...a.time, ...b.time]) time.set(e.id, e);

  const goals = new Map<string, RoadmapGoal>();
  for (const g of [...a.goals, ...b.goals]) {
    const seen = goals.get(g.roadmapId);
    if (!seen || g.updatedAt > seen.updatedAt) goals.set(g.roadmapId, g);
  }

  return {
    progress: [...progress.values()].sort((x, y) => x.doneAt.localeCompare(y.doneAt)),
    time: [...time.values()].sort((x, y) => x.at.localeCompare(y.at)),
    goals: [...goals.values()],
  };
}

function normalise(raw: unknown): RoadmapState {
  const s = (raw ?? {}) as Partial<RoadmapState>;
  return {
    progress: Array.isArray(s.progress) ? s.progress : [],
    time: Array.isArray(s.time) ? s.time : [],
    goals: Array.isArray(s.goals) ? s.goals : [],
  };
}

export type SyncStatus = "idle" | "syncing" | "synced" | "error";

/**
 * Pulls the remote record, merges it with the device record, writes the result
 * to both. Safe to call on every sign-in and after every change.
 */
export async function syncRoadmapState(userId: string): Promise<RoadmapState | null> {
  try {
    const { data, error } = await supabase
      .from("roadmap_state")
      .select("state")
      .eq("user_id", userId)
      .maybeSingle();
    if (error) throw error;

    const merged = mergeState(normalise(data?.state), localState());

    writeProgressLog(merged.progress);
    writeTimeLog(merged.time);
    writeGoals(merged.goals);

    const { error: upsertError } = await supabase
      .from("roadmap_state")
      .upsert(
        { user_id: userId, state: merged as unknown as never },
        { onConflict: "user_id" },
      );
    if (upsertError) throw upsertError;

    return merged;
  } catch (e) {
    console.error("roadmap sync failed", e);
    return null;
  }
}

/** Pushes the device record without pulling — used after a local change. */
export async function pushRoadmapState(userId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from("roadmap_state")
      .upsert(
        { user_id: userId, state: localState() as unknown as never },
        { onConflict: "user_id" },
      );
    if (error) throw error;
    return true;
  } catch (e) {
    console.error("roadmap push failed", e);
    return false;
  }
}
